import {
  origenDe,
  resumenOferta,
  segmentoDeOrigen,
  SEGMENTOS,
} from "../data/segmentos";

const FUNCTION_ENDPOINTS = ["/.netlify/functions/send-contact", "/api/send-contact"];

/**
 * Asunto, servicio y resumen de oferta: DERIVADOS, no escritos a mano.
 *
 * Antes del pivote estos tres mapas repetían los precios de fotografía que ya
 * estaban en el catálogo, y la función serverless los repetía una tercera
 * vez. Cambiar un precio y olvidar uno de los sitios hacía que el correo al
 * cliente citara un valor que la web ya no mostraba.
 *
 * Ahora un segmento nuevo en data/segmentos.js obtiene automáticamente su
 * asunto, su etiqueta de servicio y su resumen de precios, sin tocar este
 * archivo.
 */
const SUBJECTS = Object.fromEntries(
  SEGMENTOS.map((segmento) => [
    origenDe(segmento.id),
    `Cotización de ${segmento.nombre.toLowerCase()}`,
  ])
);

const SERVICE_BY_ORIGEN = Object.fromEntries(
  SEGMENTOS.map((segmento) => [origenDe(segmento.id), segmento.nombre])
);

/** El resumen de precios se compone al vuelo desde los packs del segmento. */
const offerDeOrigen = (origen) => {
  const segmento = segmentoDeOrigen(origen);
  if (!segmento) {
    return {
      presupuesto: "Por definir según el trabajo",
      promocion: "Sin promoción activa",
    };
  }
  return {
    presupuesto: resumenOferta(segmento.id),
    promocion: "Sin promoción activa",
  };
};

export const validateLead = ({ name, email, whatsapp }) => {
  if (!name.trim()) return "Por favor, ingresa tu nombre.";
  if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) return "Por favor, ingresa un correo válido.";
  if (!whatsapp.trim()) return "Por favor, ingresa tu WhatsApp o teléfono.";
  return null;
};

export const toChileWhatsApp = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("56")) return digits;
  if (digits.startsWith("9") && digits.length === 9) return `56${digits}`;
  return `56${digits}`;
};

export const buildLeadPayload = ({
  name,
  email,
  whatsapp,
  origen,
  company = "",
  businessType = "",
  channels = [],
  message = "",
  pack = "",
  rubro = "",
  cantidad = "",
  unidad = "",
}) => {
  const ahora = new Date();
  const telefono = toChileWhatsApp(whatsapp);
  const isFullForm = Boolean(company || businessType || channels.length);
  const tipoNegocio = businessType || (company ? "No indicado" : "Se levanta en WhatsApp");
  const canalesTexto = channels.join(", ") || (company ? "No indicado" : "Se levanta en WhatsApp");
  const subject = SUBJECTS[origen] || "Consulta de fotografía";
  const servicio = SERVICE_BY_ORIGEN[origen] || "Fotografía profesional";
  const offer = offerDeOrigen(origen);
  /*
    `cantidad` es el dato que decide el ticket: 12 platos y 40 platos son dos
    trabajos distintos con dos precios distintos, y hasta ahora había que
    preguntarlo por WhatsApp en todos los leads.

    Viaja en el texto del detalle y como campo propio del payload del lead,
    pero NO como columna de Supabase: omega_leads tiene columnas fijas y un
    insert con una columna que no existe falla, lo que se llevaría por
    delante el lead entero.
  */
  const escala = cantidad ? `${cantidad}${unidad ? ` ${unidad}` : ""}` : "";
  const detalle = [
    pack ? `Pack de interés: ${pack}` : "",
    escala ? `Volumen indicado: ${escala}` : "",
    rubro ? `Rubro: ${rubro}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return {
    id_lead: `OMG-${ahora.getTime().toString(36).toUpperCase()}`,
    fecha: ahora.toLocaleString("es-CL", {
      timeZone: "America/Santiago",
      dateStyle: "short",
      timeStyle: "short",
    }),
    nombre: name.trim(),
    name: name.trim(),
    empresa: company.trim() || "No indicado",
    company: company.trim(),
    whatsapp: whatsapp.trim(),
    telefono,
    phone: whatsapp.trim(),
    email: email.trim(),
    correo: email.trim(),
    tipoNegocio,
    canales: channels,
    canalesTexto,
    servicio,
    subject,
    pack: pack || "",
    rubro: rubro || "",
    cantidad: cantidad || "",
    unidad: unidad || "",
    escala,
    presupuesto: offer.presupuesto,
    promocion: offer.promocion,
    mensaje:
      message ||
      detalle ||
      (isFullForm
        ? `Empresa: ${company || "No indicado"}\nTipo de negocio: ${tipoNegocio}\nWhatsApp: ${whatsapp}\nCanales: ${canalesTexto}`
        : "Lead corto: nombre, correo y WhatsApp. Empresa, tipo de negocio y canales se levantan en la conversación."),
    whatsappLink: telefono ? `https://wa.me/${telefono}` : "",
    origen: origen || "sitio-omega",
  };
};

const postJson = async (url, payload) => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  return { response, data };
};

/**
 * Un solo camino: la función de Netlify.
 *
 * Antes el navegador hacía tres cosas en cascada: insertaba en Supabase,
 * llamaba a n8n directo y, si fallaba, llamaba a la función, que volvía a
 * insertar y a llamar a n8n. En septiembre de 2026 venció la prueba gratis
 * de n8n y los caminos cayeron juntos. El insert desde el navegador, además,
 * nunca funcionó en producción: la clave anon no estaba en el bundle, y la
 * tabla omega_leads tenía cero filas.
 *
 * Ahora la función guarda en Supabase y manda el correo. El navegador no
 * toca la base de datos ni necesita ninguna clave.
 */
export const submitLead = async (fields) => {
  const lead = buildLeadPayload(fields);

  const functionPayload = {
    name: fields.name.trim(),
    email: fields.email.trim(),
    whatsapp: fields.whatsapp.trim(),
    phone: fields.whatsapp.trim(),
    origen: fields.origen,
    id_lead: lead.id_lead,
    subject: SUBJECTS[fields.origen] || "Consulta de fotografía",
    // Se envían resueltos para que send-contact.js no tenga que mantener su
    // propia copia de los precios: sus tablas quedan solo como fallback.
    servicio: lead.servicio,
    presupuesto: lead.presupuesto,
    company: (fields.company || "").trim(),
    businessType: (fields.businessType || "").trim(),
    channels: fields.channels || [],
    message: fields.message || "",
    pack: fields.pack || "",
    rubro: fields.rubro || "",
    cantidad: fields.cantidad || "",
    unidad: fields.unidad || "",
  };

  let lastError = "Error al enviar el mensaje";

  for (const url of FUNCTION_ENDPOINTS) {
    try {
      const { response, data } = await postJson(url, functionPayload);
      if (response.ok && data.success) {
        /* La funcion responde 200 tambien cuando guardo el lead pero no pudo
           avisar. Para el visitante es un envio correcto -- su mensaje
           existe -- y el detalle queda en consola y en los logs de Netlify. */
        if (data.advertencia) console.warn("Lead guardado sin aviso:", data.advertencia);
        return data;
      }
      lastError = data.error || lastError;

      /*
        En el servidor de desarrollo (npm start) esta ruta no existe: devuelve
        el index.html con 200, el .json() falla y el envio parece roto sin que
        lo este. Se dice explicitamente para no salir a buscar un bug de
        produccion que no hay.
      */
      if (response.status === 200 && !data.success) {
        console.warn(
          "El endpoint " + url + " devolvio HTML en vez de JSON. Si estas en " +
            "npm start, es lo esperado: las funciones de Netlify no corren en el " +
            "servidor de desarrollo. Usa 'netlify dev' para probar el formulario."
        );
      }
      if (response.status !== 404) break;
    } catch (error) {
      lastError = error.message || lastError;
    }
  }

  throw new Error(lastError);
};
